import { Controller } from "@hotwired/stimulus"

// Date rail on the Subscriptions page (recurring_expenses/_calendar). One
// lens sits behind the picked tile and glides to the next one on tap. Tiles
// load only the recurring_day frame below the rail, so the rail itself is
// never rebuilt by a tap and the glide runs start to finish uninterrupted.
export default class extends Controller {
  static targets = ["rail", "lens", "tile"]
  static values = { monthUrl: String }

  connect() {
    const picked = this.tileTargets.find(t => t.classList.contains("is-on"))
    const focus = picked || this.tileTargets.find(t => t.dataset.focus === "true")

    // Placed, not animated: a fresh rail opens already settled.
    this.lensTarget.style.transition = "none"
    if (focus) this.railTarget.scrollLeft = this._centerOf(focus)
    if (picked) this._placeLens(this._xOf(picked))
    this._showLens(!!picked)
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.lensTarget.style.transition = ""
    }))
  }

  // Answers the tap before the frame does: the highlight moves now, the list
  // follows when the response lands.
  //
  // The tapped tile keeps its own look while the lens travels and only takes
  // the picked look (.is-landed) once the lens is under it; the tile it left
  // gets its look back straight away, as the lens slides off it.
  pick(event) {
    const tile = event.currentTarget
    const unpick = tile.classList.contains("is-on")
    const wasShown = this.lensTarget.classList.contains("is-shown")
    clearTimeout(this.landTimer)
    this.tileTargets.forEach(t => {
      t.classList.toggle("is-on", !unpick && t === tile)
      t.classList.remove("is-landed")
    })
    // The rail is not re-rendered by a tap, so the href it was drawn with is
    // stale after the first one. Point this tap at what it means now: the
    // day, or the whole month when it unpicks. Turbo reads the href after
    // this handler, on the click's way up to the document.
    tile.href = unpick ? this.monthUrlValue : tile.dataset.dayUrl
    if (unpick) return this._showLens(false)

    // From nothing picked, the lens fades in on the tile rather than flying
    // in from wherever it was last parked.
    if (!wasShown) {
      this.lensTarget.style.transition = "none"
      this._placeLens(this._xOf(tile))
      this.lensTarget.getBoundingClientRect()
      this.lensTarget.style.transition = ""
    } else {
      this._placeLens(this._xOf(tile))
    }
    this._showLens(true)

    // Lands when the glide ends: the lens's transform transition length, read
    // from CSS so the two can't drift. A timer, not transitionend, because a
    // tap on the tile the lens already sits under starts no transition.
    this.landTimer = setTimeout(() => {
      if (tile.classList.contains("is-on")) tile.classList.add("is-landed")
    }, this._glideMs())
  }

  disconnect() {
    clearTimeout(this.landTimer)
  }

  _glideMs() {
    const seconds = parseFloat(getComputedStyle(this.lensTarget).transitionDuration) || 0
    return seconds * 1000
  }

  _placeLens(x) {
    this.lensTarget.style.transform = `translate3d(${x}px, 0, 0)`
  }

  _showLens(on) {
    this.lensTarget.classList.toggle("is-shown", on)
  }

  // Offset inside the scrolling rail, measured on screen so padding and
  // scroll position can never put the lens beside its tile.
  _xOf(tile) {
    return tile.getBoundingClientRect().left - this.railTarget.getBoundingClientRect().left + this.railTarget.scrollLeft
  }

  _centerOf(tile) {
    return this._xOf(tile) - (this.railTarget.clientWidth - tile.offsetWidth) / 2
  }
}
